// The 10 currencies supported across Yalola (agreed product list).
export const CURRENCIES = [
  { code: "EUR", label: "Euro (€)", symbol: "€" },
  { code: "USD", label: "Dollar US ($)", symbol: "$" },
  { code: "GBP", label: "Livre sterling (£)", symbol: "£" },
  { code: "CHF", label: "Franc suisse (CHF)", symbol: "CHF" },
  { code: "CAD", label: "Dollar canadien (C$)", symbol: "C$" },
  { code: "XOF", label: "Franc CFA Ouest (XOF)", symbol: "FCFA" },
  { code: "XAF", label: "Franc CFA Central (XAF)", symbol: "FCFA" },
  { code: "CDF", label: "Franc congolais (FC)", symbol: "FC" },
  { code: "NGN", label: "Naira nigérian (₦)", symbol: "₦" },
  { code: "ZAR", label: "Rand sud-africain (R)", symbol: "R" },
];

export const CURRENCY_CODES = CURRENCIES.map((c) => c.code);

export const currencyLabel = (code) =>
  (CURRENCIES.find((c) => c.code === code) || {}).label || code;

// Libellé dans la langue de l'interface (« US Dollar ($) », « Dólar estadounidense ($) »…) via Intl.DisplayNames ;
// repli sur le libellé français si le moteur ne connaît pas la devise (XOF/XAF/CDF selon la WebView).
export const localizedCurrencyLabel = (code, lang = "fr") => {
  const c = CURRENCIES.find((x) => x.code === code);
  if (!c) return code;
  try {
    const name = new Intl.DisplayNames([lang], { type: "currency" }).of(code);
    if (name && name !== code) return `${name.charAt(0).toUpperCase()}${name.slice(1)} (${c.symbol})`;
  } catch { /* Intl.DisplayNames absent */ }
  return c.label;
};

// Country (ISO 3166-1 alpha-2) → one of the 10 supported currencies.
// Only countries whose local currency is supported are listed; others fall back.
export const COUNTRY_CURRENCY = {
  // Eurozone
  DE: "EUR", AD: "EUR", AT: "EUR", BE: "EUR", CY: "EUR", EE: "EUR", ES: "EUR", FI: "EUR",
  FR: "EUR", GR: "EUR", HR: "EUR", IE: "EUR", IT: "EUR", LV: "EUR", LT: "EUR", LU: "EUR",
  MT: "EUR", MC: "EUR", ME: "EUR", NL: "EUR", PT: "EUR", SK: "EUR", SI: "EUR",
  // US dollar (official or de-facto)
  US: "USD", EC: "USD", SV: "USD", PA: "USD",
  GB: "GBP",
  CH: "CHF",
  CA: "CAD",
  // West African CFA franc
  BJ: "XOF", BF: "XOF", CI: "XOF", GW: "XOF", ML: "XOF", NE: "XOF", SN: "XOF", TG: "XOF",
  // Central African CFA franc
  CM: "XAF", CF: "XAF", CG: "XAF", TD: "XAF", GQ: "XAF", GA: "XAF",
  CD: "CDF",
  NG: "NGN",
  // Rand zone
  ZA: "ZAR", LS: "ZAR", NA: "ZAR",
};

export const currencyForCountry = (code) => COUNTRY_CURRENCY[(code || "").toUpperCase()] || null;

// Management currency mode: the whole platform runs in ONE currency (the admin
// "Devise de gestion"). Country/browser/user preference are intentionally ignored
// so every amount (dons, offrandes, vérifications, pubs, dépenses, revenu net) is
// expressed in the same currency — no conversion, coherent totals.
export const autoCurrency = ({ defaultCurrency } = {}) =>
  (CURRENCY_CODES.includes(defaultCurrency) ? defaultCurrency : "EUR");

// Suggested one-tap offering/donation amounts, scaled to each currency's magnitude
// (5 € makes sense; 5 FC does not). Falls back to the EUR-style tier.
// 4 pastilles : le plus petit don en premier (= défaut), le champ libre couvre le reste.
const AMOUNT_TIERS = {
  EUR: [1, 5, 10, 20],
  USD: [1, 5, 10, 20],
  GBP: [1, 5, 10, 20],
  CHF: [1, 5, 10, 20],
  CAD: [1, 5, 10, 20],
  ZAR: [10, 50, 100, 200],
  XOF: [500, 1000, 2000, 5000],
  XAF: [500, 1000, 2000, 5000],
  NGN: [500, 1000, 2000, 5000],
  CDF: [1000, 2000, 5000, 10000],
};

export const suggestedAmounts = (code) => AMOUNT_TIERS[code] || AMOUNT_TIERS.EUR;
